// ============================================================================
// ui/shell/product-down-tab.js — the product switcher BAR (2026-07-17)
// ============================================================================
// A single floating glass bar naming the live product with a ‹ › arrow each side
// to scroll the buyer's picked set. It lives in the BOTTOM band of the canvas —
// level with the powered-by badge, in the dead space the floor-clamp (orbit can't
// dip below 1° off the deck) guarantees is always clear of the model + shadow
// (Ryan 2026-07-17: "it could sit in this space below ... about as high as the
// POWERED BY text, and look really sharp/cohesive down there"). The 3D model is
// nudged UP to sit optically centred in the room ABOVE the bar (a camera
// lens-shift — main.js syncBandShift → reveal.setBandShift — plus the flat-SVG
// `--wd-plate-y` mirror in weight-designer.css, driven off this bar's visibility).
//
// HISTORY — the "down-tab" name is kept to avoid churn across main.js, the CSS
// (`.wd-product-tab*`) and the drivers, but it is no longer a tab that hangs
// down: it first replaced the top-left `.wd-product-switch` glass pill (which
// rode `.wd-pane-follow` and slid AWAY under the left pane, leaving no product
// control when the pane was collapsed), then briefly hung off the TOP rim as the
// step tabs' mirror — until that top band was wanted for the 3D and the status
// toast, and the bottom band read cleaner and more cohesive. It still re-centres
// over the VISIBLE stage as the pane moves (rides `--wd-pane-half`), so the pane
// can never cover it.
//
// SAME API (`{el, setActive, setProducts}`) so it drops into main.js's five
// touch-points unchanged. Like the old pill it hides itself below two products:
// a switcher between one thing is furniture — and its absence is exactly what
// lets the model drop back to true centre (the band lift keys off `el.hidden`).
//
// KIND AND CATEGORY ARRIVE SEPARATELY, not as one "Competition Rubber · Bumper
// Plate" string, so that each can be its own element — below 900px the category
// is dropped in CSS so the bar stays compact and clear of the pane (Ryan
// 2026-07-17). One pre-joined string would have forced that decision into a
// matchMedia listener here, i.e. a JS re-render racing a CSS breakpoint. It still
// does no deriving of its own: main.js's `pickedProducts()` supplies both words.
//
// ONE CONSTANT WIDTH (2026-08-02). The bar used to size to the product SHOWING, so
// every ‹ › press re-sized it under the cursor — "Round Rubber · Dumbbell" is a lot
// shorter than "Straight Bar Urethane · Fixed Barbell". The phone had already been
// fixed the other way (both edges pinned + ellipsis, see the ≤768px CSS block);
// this is the desktop half.
//
// It reserves width by rendering a LAYER PER PICKED PRODUCT, all stacked in one CSS
// grid cell (`grid-area: 1/1`), with only the active one visible. A grid cell sizes
// to its widest child, so the bar is exactly as wide as the longest name in the
// picked set and cycling moves nothing. `setActive` toggles visibility; no text is
// swapped any more.
//
// Deliberately NOT a JS measurement + `min-width`:
//   · a cached px width is measured in the FALLBACK font and never re-measured when
//     the embedded webfont swaps in — the bar would settle at the wrong width;
//   · it would need a resize listener and a re-measure at each breakpoint;
//   · the ghosts obey the responsive rules for free — `.wd-product-tab-cat` is a
//     class selector, so <900px drops the category from every layer at once and the
//     reserved width shrinks with it. A measured constant would have to be redone.
// Nothing here is pegged to a name or a number: add a longer product to products.js
// and the widest layer grows on its own.
//
//   const tab = createProductDownTab({ products: [{key, kind, category}],
//                                      onSelect, matchWidthOf: stepTabs.el });
//   tab.setActive('grip|Premium Urethane');
// ============================================================================

import { h, replaceChildren } from '../dom.js';
import { syncMarquee, observeMarquee } from './marquee.js';

/**
 * @param {{
 *   products?: Array<{key: string, brand: string, kind: string, category: string}>,  // the buyer's picks, in on-screen order
 *   onSelect: (key: string) => void,
 * }} config
 * @returns {{ el: HTMLElement, setActive: (key: string|null) => void,
 *             setProducts: (p: Array<{key, brand, kind, category}>) => void }}
 */
export function createProductDownTab({ products = [], onSelect }) {
  let items = [];
  let active = null;
  /** One layer per item, index-aligned with `items`. Rebuilt only by setProducts(). */
  let layers = [];

  // The stacking cell — every layer sits in it at `grid-area: 1/1`, so its width is
  // the WIDEST layer's, not the showing one's. Empty until setProducts() fills it.
  const mid = h('span', { class: 'wd-product-tab-mid' });
  const prev = arrow('prev', '‹', 'Previous product');
  const next = arrow('next', '›', 'Next product');

  // The bar body wears the shared floating flat-glass (raised depth + lit top edge
  // + gloss) — the same tier as the tool cluster / zoom / "Show me what's possible"
  // (Ryan 2026-07-17: "add a fraction more depth like a floating button"). The
  // arrows sit ON this glass, so they stay `--flush` (glass-on-glass, per the WD
  // glass rule) — see arrow() above.
  const tab = h('div', { class: 'wd-product-tab wd-flat-glass' }, prev, mid, next);
  const el = h('div', { class: 'wd-product-tab-row', role: 'group', 'aria-label': 'Product' }, tab);

  function arrow(dir, glyph, aria) {
    return h(
      'button',
      {
        type: 'button',
        class: 'wd-product-tab-arrow wd-flat-glass wd-flat-glass--flush',
        'aria-label': aria,
        // The ← / → hint is a `title`, not part of the label: a screen-reader user is not
        // driving this with arrow keys and does not need "Next product, right arrow" read out.
        title: `${aria} (${dir === 'next' ? '→' : '←'})`,
        'aria-keyshortcuts': dir === 'next' ? 'ArrowRight' : 'ArrowLeft',
        onClick: () => step(dir === 'next' ? 1 : -1),
      },
      h('span', { 'aria-hidden': 'true' }, glyph),
    );
  }

  /**
   * One product's row: "2/3" then "Brand · Kind · Category".
   *
   * The segments are three elements with two separator spans between them, and the
   * separators are their OWN elements (not baked into a segment's text) so a
   * breakpoint can drop a segment AND its adjoining dot in pure CSS: <900 drops the
   * category + sep2, <480 drops the brand + sep1 — leaving no orphan " · ". Those
   * are class selectors, so they trim the hidden layers too and the reserved width
   * shrinks at the same breakpoint. (Ryan 2026-07-17 added the brand + the counter.)
   *
   * The counter lives INSIDE the layer rather than beside the stack, so its width is
   * reserved as well — otherwise a set of ten still jogged the bar on the 9/10 →
   * 10/10 step, the one digit tabular-nums cannot absorb.
   */
  function makeLayer(item, i, total) {
    // An ABSENT brand drops its span and sep1 outright, rather than rendering an
    // empty one — the same pair the <480 rule hides, for the same reason (no
    // orphan " · " leading the label). It has to happen here and not in CSS
    // because the brand is empty by CONFIGURATION, not by viewport: the house
    // line is unannounced until its product pages ship (src/brand-flag.js), and
    // an empty span would still leave the label's gap reserving width on every
    // ghost layer in the constant-width grid.
    const brandParts = item.brand
      ? [
          h('span', { class: 'wd-product-tab-brand' }, item.brand),
          h('span', { class: 'wd-product-tab-sep wd-product-tab-sep1', 'aria-hidden': 'true' }, ' · '),
        ]
      : [];
    /* ── THE PARTS MOVED INSIDE A WINDOW (Ryan 2026-08-09) ────────────────────────────────────
       *"narrow phone screens might result in the words on this bar now truncating, so we need to
       have them auto-scroll in the same way they do on the left and right back/next pills."*
       Which they now do, because the "?" joined the tool cluster and this bar is anchored to it —
       every screen gives the label whatever the cluster leaves, and a fourth button took 46px of it.
         `.wd-product-tab-label` stays the WINDOW (it already clips and ellipsizes); the parts get an
       inner span to travel inside it. Same two class names, same `.is-scrolling` contract and the
       same stylesheet rules as the step nav — see ui/shell/marquee.js. */
    const label = h(
      'span',
      { class: 'wd-product-tab-label' },
      h('span', { class: 'wd-product-tab-labeltext' },
        /* ⭐ THE PLATE-COLOUR DOT — the ONLY thing that tells two configurations of one product
           apart (Ryan 2026-08-09). A school with white 45s and green 25s has two pills reading the
           identical "Competition Urethane · Bumper Plate", and without this the switcher is asking
           them to pick between two things it refuses to distinguish.
             ⚠ PRESENT ONLY WHEN THERE IS SOMETHING TO DISTINGUISH. `item.swatch` is set by
           `pickedProducts()` solely for a product picked more than once, so every existing set
           renders byte-identical markup — which is what keeps this out of the way of the bar's
           constant-width grid and of the exact-pixel geometry `probe-product-bar.mjs` pins. */
        ...(item.swatch
          ? [h('span', {
            class: 'wd-product-tab-swatch',
            style: `--wd-tab-swatch: ${item.swatch}`,
            'aria-hidden': 'true',
          })]
          : []),
        ...brandParts,
        h('span', { class: 'wd-product-tab-kind' }, item.kind || ''),
        h('span', { class: 'wd-product-tab-sep wd-product-tab-sep2', 'aria-hidden': 'true' }, ' · '),
        h('span', { class: 'wd-product-tab-cat' }, item.category || '')),
    );
    // Position within the picked set, so a buyer knows how many they picked and where
    // they are. aria-hidden: the label already announces each product on switch.
    const count = h('span', { class: 'wd-product-tab-count', 'aria-hidden': 'true' }, `${i + 1}/${total}`);
    return h('span', { class: 'wd-product-tab-layer', 'aria-hidden': 'true' }, count, label);
  }

  /**
   * Which WAY the last move went, so `render` can swipe the label the matching direction.
   *
   * Set by `step`/`stepBy` — the two arrows and the ←/→ keys — and consumed once. Left null by any
   * other caller of `setActive` (main.js re-homing the bar after a pick was removed, a resume
   * landing on a product), where `render` falls back to comparing the two INDEXES. Both answers
   * exist because neither is complete on its own: an index delta cannot tell a wrap-around from a
   * long jump backwards, and an arrow press does not reach `setActive` directly — it goes out
   * through `onSelect`, into `switchProduct`, and comes back several awaits later.
   */
  let pendingDir = 0;

  /** Wrap around the picked set, left-most-first — the order `pickedProducts()` yields. */
  function step(dir) {
    if (items.length < 2) return;
    pendingDir = dir;
    const at = items.findIndex((p) => p.key === active);
    // No active product (a blank stage) → an arrow enters the set at its end,
    // rather than doing nothing.
    const from = at < 0 ? (dir > 0 ? -1 : 0) : at;
    const to = (from + dir + items.length) % items.length;
    onSelect(items[to].key);
  }

  // Show the active layer, hide the rest. No text is written here — the layers own
  // their words from birth, so a switch is a visibility flip and nothing reflows.
  //
  // `visibility: hidden` (in CSS) is the load-bearing choice: `display: none` would
  // drop the ghost out of layout and take the reserved width with it, and `opacity:
  // 0` would leave it clickable and readable. The explicit `aria-hidden` on top is
  // belt-and-braces for AT that walks the DOM rather than the render tree — nobody
  // should hear all four product names at once.
  //
  // A blank stage (`setActive(null)`) now shows no layer but keeps the bar's shape,
  // where the old version blanked every span and collapsed it to a glass stub.
  function render() {
    const at = items.findIndex((p) => p.key === active);
    /* ── THE LABEL SWIPES (Ryan 2026-08-07) ──────────────────────────────────────────────────────
     * *"when you click the right arrow, can you have the text for the current product name slide
     * left and the new text for the new model slide in from the right? Vice-versa when you click
     * the left arrow?"*
     *
     * The layers are already stacked in one grid cell (see the header) — the bar's constant width
     * depends on it — so the swipe is two animations on two siblings, not a re-render.
     *
     * ⚠ THE OUTGOING LAYER HAS TO STAY VISIBLE TO LEAVE. `.is-active` is a `visibility` flip, which
     * is instant by definition; a layer that loses the class simply vanishes with nowhere to go. So
     * the one being left keeps a hold on visibility through `.is-leaving` and drops it when its
     * animation ends.
     *
     * ⚠ AND WEB ANIMATIONS, NOT CLASSES + `transitionend`, for the reason `setTitle` documents: this
     * bar is SCRUBBED. Ryan holds an arrow down or clicks it four times in a second, and a
     * class-based animation mid-flight has to be removed, reflowed and re-added to restart — while
     * a stuck class leaves a label parked off-centre for good. An animation object can just be
     * cancelled. */
    const from = lastAt;
    lastAt = at;
    const dir = swipeDir(from, at);
    layers.forEach((layer, i) => {
      const on = i === at;
      if (dir && i === from && from !== at) playSwipe(layer, dir, 'out');
      layer.classList.toggle('is-active', on);
      if (on) layer.removeAttribute('aria-hidden');
      else layer.setAttribute('aria-hidden', 'true');
      if (dir && on && from !== at && from >= 0) playSwipe(layer, dir, 'in');
    });
    // Nothing to switch BETWEEN → no bar. Hidden rather than emptied so it can't
    // leave a bare glass stub floating in the bottom band.
    el.hidden = items.length < 2;
    /* ⚠ ON THE NEXT FRAME, and only for the layer now showing. The ghost layers are
       `visibility: hidden` and still laid out, so they measure fine — but a marquee running on
       four labels nobody can see is three animations of pure cost, and the widths were written
       microseconds ago so `getBoundingClientRect` here would still describe the previous paint.
       Same reason `syncNavMarquee` waits a frame. */
    requestAnimationFrame(syncLabelMarquee);
  }

  /** The visible layer's label, measured for overflow. See ui/shell/marquee.js. */
  function syncLabelMarquee() {
    const at = items.findIndex((p) => p.key === active);
    layers.forEach((layer, i) => {
      syncMarquee(layer.querySelector('.wd-product-tab-label'), el.hidden || i !== at);
    });
  }

  /** Which index was showing last, so a swipe knows where it came from. −1 = nothing yet. */
  let lastAt = -1;
  const TAB_SWIPE_PX = 14;   // a nudge — the bar is narrow and a long slide would cross the arrows

  /**
   * +1 = the new product is to the RIGHT of the old one (label travels leftward), −1 = the reverse,
   * 0 = do not animate (first paint, or the set changed under us).
   *
   * An arrow press wins outright, because it is the only source that knows a wrap-around from a
   * jump: stepping `›` off the last item lands on index 0, whose delta says "went backwards".
   */
  function swipeDir(from, to) {
    if (from < 0 || to < 0 || from === to) { pendingDir = 0; return 0; }
    if (pendingDir) { const d = pendingDir; pendingDir = 0; return d > 0 ? 1 : -1; }
    return to > from ? 1 : -1;
  }

  /** One half of the swipe on one layer. `phase` is 'out' (leaving) or 'in' (arriving). */
  function playSwipe(layer, dir, phase) {
    if (typeof layer.animate !== 'function') return;
    if (reducedMotion && reducedMotion.matches) return;
    if (layer._anim) layer._anim.cancel();
    const away = `translateX(${-dir * TAB_SWIPE_PX}px)`;
    const near = `translateX(${dir * TAB_SWIPE_PX}px)`;
    const spec = phase === 'out'
      // Faster out than in, the same asymmetry the pane title and the sheet use: leaving should be
      // brisk, arriving should settle.
      ? { frames: { transform: ['translateX(0)', away], opacity: [1, 0] }, ms: 130, ease: 'cubic-bezier(0.4, 0, 1, 1)' }
      : { frames: { transform: [near, 'translateX(0)'], opacity: [0, 1] }, ms: 190, ease: 'cubic-bezier(0.2, 0, 0, 1)' };
    if (phase === 'out') layer.classList.add('is-leaving');
    const anim = layer.animate(spec.frames, { duration: spec.ms, easing: spec.ease });
    layer._anim = anim;
    anim.finished
      .then(() => { if (layer._anim === anim) { layer._anim = null; layer.classList.remove('is-leaving'); } })
      // Cancelled by a newer swipe on this same layer — that one owns it now, and it will clear the
      // class itself. Dropping it here would un-hide a layer mid-flight.
      .catch(() => {});
  }

  const reducedMotion =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;

  /**
   * Swap the list (the "Pick Weights" tab can change the picks mid-session).
   * The only place `items` changes, so it is also the only place the layers are
   * built — they cannot drift out of step with the list they size the bar to.
   */
  function setProducts(nextItems) {
    // The layers are about to be rebuilt, so any index remembered against the OLD list is
    // meaningless — and a swipe computed from it would fly the wrong label the wrong way.
    lastAt = -1;
    pendingDir = 0;
    items = Array.isArray(nextItems) ? nextItems : [];
    layers = items.map((item, i) => makeLayer(item, i, items.length));
    replaceChildren(mid, layers);
    render();
  }

  /** Fires on every product swap — and with null when the stage goes blank. */
  function setActive(key) {
    active = key;
    render();
  }

  // The bar sizes to the LONGEST NAME IN THE PICKED SET (the layer stack above),
  // centred in the bottom band — it no longer matches the step strip's width (that
  // made sense when it hung off the same rim as the tabs; down here it's its own
  // control). See `.wd-product-tab-row` for the centring + the responsive segment
  // drops, and `.wd-product-tab-mid` / `-layer` for the stack itself.

  /**
   * Step the picked set from OUTSIDE the bar — today the ←/→ keys (main.js owns the guards that
   * decide when a keypress means "next product"; this owns what that means).
   *
   * ⛔ IT CALLS `step`, THE SAME FUNCTION THE ARROW BUTTONS CALL. Not a copy of the wrap-around
   * arithmetic — the modulo, the "no active product enters the set at its end" case and the
   * two-item floor are one implementation or they are two that will disagree. CLAUDE.md §0b.
   *
   * `pulse` flashes the arrow the key corresponds to, so the keyboard visibly drives the on-screen
   * control rather than the product changing with nothing to explain it. Purely cosmetic, and
   * deliberately fired here rather than by the caller: the bar owns its own chrome.
   */
  function stepBy(dir) {
    if (items.length < 2) return false;
    pendingDir = dir;
    pulse(dir > 0 ? next : prev);
    step(dir > 0 ? 1 : -1);
    return true;
  }

  /** A brief pressed state. Re-triggerable: remove, force a reflow, re-add — without the reflow a
   *  second press inside the window re-adds a class the element never lost and nothing animates. */
  function pulse(btn) {
    btn.classList.remove('is-keypulse');
    void btn.offsetWidth;
    btn.classList.add('is-keypulse');
    setTimeout(() => btn.classList.remove('is-keypulse'), 220);
  }

  setProducts(products);
  /* The bar is anchored between the canvas gutter and the tool cluster, so its width moves with the
     screen rather than with its own text — a rotation, a pane opening, a late web font. Every one of
     those changes whether the label overflows. */
  observeMarquee(el, syncLabelMarquee);
  return {
    el,
    setActive,
    /**
     * Where an "added to your selection" ghost should land — the ACTIVE layer's `1/3` counter.
     *
     * ⚠ The active layer is the one carrying `.is-active`; the others are `visibility: hidden` and
     * still laid out, so they measure perfectly and would be a plausible-looking wrong answer.
     * ⚠ Returns null while the bar is hidden (fewer than two products). A hidden element still has
     * a rect — all zeros — so a caller that only null-checked the ELEMENT would fly a ghost to the
     * top-left corner of the screen on the very first pick.
     */
    flyTarget() {
      if (el.hidden) return null;
      return el.querySelector('.wd-product-tab-layer.is-active .wd-product-tab-count')
        || el.querySelector('.wd-product-tab-count');
    },
    /**
     * Repaint ONE product's plate-colour dot, in place.
     *
     * ⚠ NOT `setProducts` WITH FRESH COLOURS, which is the obvious way and is wrong twice over.
     * That rebuilds every layer — so it would restart the label's swipe animation and re-measure
     * the constant-width grid, on every frame of a hue-bar drag. This writes one custom property
     * on one node, which is why it is cheap enough to live on the render pipeline's paint step and
     * therefore to track a live colour PREVIEW rather than only a committed one.
     *
     * No-ops on a set with no dots, which is every set that does not have a product picked twice.
     */
    setSwatch: (key, hex) => {
      if (!key || !hex) return;
      const i = items.findIndex((p) => p.key === key);
      if (i < 0) return;
      const dot = layers[i] && layers[i].querySelector('.wd-product-tab-swatch');
      if (dot) dot.style.setProperty('--wd-tab-swatch', hex);
    },
    setProducts,
    stepBy,
    /** Is there anything to step BETWEEN? The bar's own visibility rule, asked rather than
     *  re-derived — main.js gates the keys on exactly what makes the bar appear. */
    canStep: () => items.length >= 2 && !el.hidden,
  };
}
